import { format, parseISO } from 'date-fns';
import { formatEther } from 'ethers/lib/utils';
import { Trans, useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useCallback, VFC, useContext, useEffect, useState } from 'react';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { MintBidWithUser } from '@/apis/aniftyBackend/mintAsks/interface';
import FramedIcon from '@/components/FramedIcon';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useETHPrice from '@/hooks/useETHPrice';

import styles from './CreationAuction.module.scss';

type Props = {
  mintBids: MintBidWithUser[];
  creation: V2CreationData;
  listedPrice: string;
  isFinished: boolean;
  highestBid: MintBidWithUser;
};

const AuctionHistory: VFC<Props> = ({
  mintBids,
  creation,
  listedPrice,
  isFinished,
  highestBid,
}) => {
  const { t, i18n } = useTranslation();
  const [isArtist, setIsArtist] = useState(false);
  const { convertETHToLocalCurrency } = useETHPrice();

  const getDisplayName = (user) => {
    if (user.displayNameJA && i18n.language === 'ja') {
      return user.displayNameJA;
    } else if (user.displayName) {
      return user.displayName;
    } else {
      return user.userID;
    }
  };

  const formattedTimeStamp = useCallback((isoTimestamp: string) => {
    if (!isoTimestamp) return null;

    return format(parseISO(isoTimestamp), "MMM dd, yyyy 'at' hh:mm aa");
  }, []);

  const web3Context = useContext(Web3Context);

  useEffect(() => {
    const isCreator =
      web3Context?.currentAccountAddress &&
      creation?.creator?.userID &&
      web3Context.currentAccountAddress.toLowerCase() ===
        creation.creator.userID.toLowerCase();
    setIsArtist(isCreator);
  }, [web3Context, creation]);

  return (
    <div>
      <div className={styles.historyTitleContainer}>
        <p className={styles.historyTitle}>HISTORY</p>
        <div className={styles.moreInfo}>
          {(isArtist && (
            <div className={styles.moreInfoText}>
              {t('auctionInfoMessage.artist')}
            </div>
          )) || (
            <div className={styles.moreInfoText}>
              {t('auctionInfoMessage.collector')}
            </div>
          )}
          <p className={styles.moreInfoIcon}>?</p>
        </div>
      </div>

      {/* winner */}
      {isFinished && highestBid && (
        <div className={styles.historyWinner}>
          <div className={styles.historyWinnerContainer}>
            <Link href={routes.myPage(highestBid.user.userID)}>
              <a>
                <FramedIcon
                  imageURL={
                    highestBid.user.profileImageURL ??
                    DEFAULT_PROFILE_IMAGE_PATH
                  }
                />
              </a>
            </Link>

            <div className={styles.historyBidderContent}>
              <p className={styles.userAction}>
                <Trans
                  i18nKey="creationPage.bidHistory.wonBy"
                  components={{
                    strong: <strong />,
                  }}
                  values={{
                    userName: getDisplayName(highestBid.user),
                  }}
                />
              </p>

              <p className={styles.historyTimestamp}>
                {formattedTimeStamp(highestBid.bidAt)}
              </p>
            </div>
          </div>

          <div className={styles.historyPrice}>
            <p className={styles.historyEthPrice}>
              {formatEther(highestBid.price)} WETH
            </p>
            <p className={styles.historyDollarPrice}>
              {convertETHToLocalCurrency(highestBid.price)}
            </p>
          </div>
        </div>
      )}

      <ul className={styles.bidsHistory}>
        {/* bid by */}
        {mintBids.map((bid, i) => (
          <li key={i}>
            <div className={styles.historyBidder}>
              <Link href={routes.myPage(bid.user.userID)}>
                <a>
                  <FramedIcon
                    imageURL={
                      bid.user.profileImageURL ?? DEFAULT_PROFILE_IMAGE_PATH
                    }
                  />
                </a>
              </Link>
              <div className={styles.historyBidderContent}>
                <p className={styles.userAction}>
                  <Trans
                    i18nKey="creationPage.bidHistory.bidBy"
                    components={{
                      strong: <strong />,
                    }}
                    values={{
                      userName: getDisplayName(bid.user),
                    }}
                  />
                </p>

                <p className={styles.historyTimestamp}>
                  {formattedTimeStamp(bid.bidAt)}
                </p>
              </div>
            </div>

            <div className={styles.historyPrice}>
              <p className={styles.historyEthPrice}>
                {formatEther(bid.price)} ETH
              </p>
              <p className={styles.historyDollarPrice}>
                {convertETHToLocalCurrency(bid.price)}
              </p>
            </div>
          </li>
        ))}
        {/* listed by */}
        <li>
          <div className={styles.historyBidder}>
            <Link href={routes.myPage(creation.creator.userID)}>
              <a>
                <FramedIcon imageURL={creation.creator.profileImageURL} />
              </a>
            </Link>

            <div className={styles.historyBidderContent}>
              <p className={styles.userAction}>
                <Trans
                  i18nKey="creationPage.bidHistory.listedBy"
                  components={{
                    strong: <strong />,
                  }}
                  values={{
                    userName: getDisplayName(creation.creator),
                  }}
                />
              </p>

              <p className={styles.historyTimestamp}>
                {formattedTimeStamp(creation.mintAsk.createdAt)}
              </p>
            </div>
          </div>

          <div className={styles.historyPrice}>
            <p className={styles.historyEthPrice}>
              {formatEther(listedPrice)} ETH
            </p>
            <p className={styles.historyDollarPrice}>
              {convertETHToLocalCurrency(listedPrice)}
            </p>
          </div>
        </li>

        {/* created by */}
        <li>
          <div className={styles.historyBidder}>
            <Link href={routes.myPage(creation.creator.userID)}>
              <a>
                <FramedIcon imageURL={creation.creator.profileImageURL} />
              </a>
            </Link>
            <div className={styles.historyBidderContent}>
              <p className={styles.userAction}>
                <Trans
                  i18nKey="creationPage.bidHistory.createdBy"
                  components={{
                    strong: <strong />,
                  }}
                  values={{
                    userName: getDisplayName(creation.creator),
                  }}
                />
              </p>

              <p className={styles.historyTimestamp}>
                {formattedTimeStamp(creation.createdAt)}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </div>
  );
};

export default AuctionHistory;
